<template>
    <div class="mapbox-map" :class="rootClasses">
        <div class="mapbox-map--window-helper" ref="windowHelper"></div>
        <div class="mapbox-map--canvas-root" v-once ref="canvasRoot"></div>
        <div class="mapbox-map--actions-root">
            <div class="mapbox-map--action mapbox-map--action__reset-camera" v-if="isHomeIntroDismissed">
                <button class="mapbox-map--button btn btn__bubble btn__light"
                        @click="onLocateActionRequested">
                    <cmp-icon symbol="locate" size="medium"></cmp-icon>
                </button>
            </div>
            <div class="mapbox-map--action mapbox-map--action__zoom-in" v-if="isHomeIntroDismissed && !isMobile">
                <button class="mapbox-map--button btn btn__bubble btn__light"
                        :disabled="isZoomInAvailable ? null : true"
                        @click="onZoomInActionRequested">
                    <cmp-icon symbol="plus" size="medium"></cmp-icon>
                </button>
            </div>
            <div class="mapbox-map--action mapbox-map--action__zoom-out" v-if="isHomeIntroDismissed && !isMobile">
                <button class="mapbox-map--button btn btn__bubble btn__light"
                        :disabled="isZoomOutAvailable ? null : true"
                        @click="onZoomOutActionRequested">
                    <cmp-icon symbol="minus" size="medium"></cmp-icon>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { geolocation } from 'modernizr';
    import { get, partial, isEqual } from 'lodash';
    import { mapGetters } from 'vuex';
    import { mapFilters } from 'util/vue/map-filters';
    import bemMixin from 'framework/mixins/bem';
    import isMountedMixin from 'framework/mixins/is-mounted';
    import isMobileMixin from 'framework/mixins/is-mobile';
    import cmsPartialMixin from 'framework/mixins/cms/cms-partial';
    import { MAP_CONFIG, MAP_ICONS } from './data';
    import * as helpers from './helpers';


    const htmlNode = document.documentElement;


    function getCoordsFromLocation(location) {
        return get(location, 'geometry.coordinates');
    }


    export default {
        mixins: [
            bemMixin('mapbox-map'),
            isMountedMixin,
            isMobileMixin,
            cmsPartialMixin,
        ],
        props: {
            partialName: {
                type: String,
                default: 'map',
            },
        },
        data() {
            return {
                isMapboxMapReady: false,
                lastLocation: this.centerLocation,
                mapZoom: null,
            };
        },
        computed: {
            ...mapGetters(['pageType', 'isHomeIntroDismissed']),
            rootClasses() {
                return [
                    this.bemAdd(this.pageType),
                    this.bemAdd(this.isHomeIntroDismissed ? 'page-home-base' : 'page-home-intro'),
                ];
            },
            isHome() {
                return this.pageType === 'page-home';
            },
            isReady() {
                return this.showPartial && this.isMounted;
            },
            isZoomInAvailable() {
                return this.mapZoom < MAP_CONFIG.maxZoom;
            },
            isZoomOutAvailable() {
                return this.mapZoom > MAP_CONFIG.minZoom;
            },
            activeLocation() {
                const location = this.getLocationByRoute(this.$route);
                this.isMapboxMapReady && helpers.setActive(location);

                return location;
            },
            centerLocation() {
                return {
                    geometry: { coordinates: this.partial.map_center },
                };
            },
            geojsonLocations() {
                return this.partial.locations.map(helpers.createGeojsonLocation);
            },
            mapIcons() {
                return MAP_ICONS.map(icon => {
                    return {
                        ...icon,
                        url: this.static(icon.url),
                    };
                });
            },
            eventCallbacks() {
                return {
                    locationClick: this.onLocationSelected,
                    locationFocus: this.onLocationFocused,
                    locationBlur: this.onLocationBlurred,
                    clusterClick: this.onClusterSelected,
                };
            },
        },
        methods: {
            ...mapFilters(['static']),
            centerOffset() {
                const wH = htmlNode.offsetHeight;
                const { offsetHeight: mH, offsetTop: mTop } = this.$refs.windowHelper;
                const dY = mTop + (mH / 2) - (wH / 2);

                return [0, dY];
            },
            getLocationByRoute(route) {
                return this.geojsonLocations.find(({ properties }) => {
                    return isEqual(this.$router.resolve(properties.link).route, route);
                });
            },
            fitBounds(config) {
                helpers.fitBounds(this.partial.map_bounds, Object.assign({}, {
                    offset: this.centerOffset(),
                    padding: this.isMobile ? 24 : 54,
                    linear: true,
                }, config));
            },
            onLocateActionRequested() {
                if (!geolocation) {
                    this.onResetActionRequested();
                    return;
                }

                window.navigator.geolocation.getCurrentPosition(
                    ({ coords }) => {
                        const { latitude, longitude } = coords;
                        const offset = this.centerOffset();

                        helpers.easeTo({
                            center: { lat: latitude, lng: longitude },
                            offset,
                        });
                    },
                    () => {
                        this.onResetActionRequested();
                    },
                );
            },
            onResetActionRequested() {
                document.activeElement.blur();
                this.fitBounds();
            },
            onZoomInActionRequested() {
                document.activeElement.blur();
                helpers.zoomBy(1);
            },
            onZoomOutActionRequested() {
                document.activeElement.blur();
                helpers.zoomBy(-1);
            },
            onClusterSelected(cluster) {
                this.$log.info('Cluster selected', cluster);

                helpers.easeTo({
                    zoom: (this.mapZoom + 2),
                    center: cluster.geometry.coordinates,
                    offset: this.centerOffset(),
                });
            },
            onLocationSelected(location) {
                this.$log.info('Location selected', location);
                this.$router.push(location.properties.link);
            },
            onLocationFocused() {
            },
            onLocationBlurred() {
            },
        },
        watch: {
            isReady: {
                handler(val) {
                    if (!val || this.isMapboxMapReady) {
                        return;
                    }

                    const EventBus = this.$emit.bind(this);
                    const curriedLoadLayers = partial(helpers.loadLayers, this.geojsonLocations);
                    const curriedAddEvents = partial(helpers.addEvents, this.eventCallbacks);

                    Promise.resolve()
                        .then(() => {
                            return helpers.loadMap({
                                container: this.$refs.canvasRoot,
                                center: getCoordsFromLocation(this.activeLocation || this.centerLocation),
                                zoom: this.activeLocation ? 15 : null,
                                //maxBounds: this.partial.map_bounds,
                            }, EventBus);
                        })
                        .then(() => {
                            const promises = this.mapIcons.map(icon => helpers.loadImage(icon));
                            return Promise.all(promises);
                        })
                        .then(curriedLoadLayers)
                        .then(curriedAddEvents)
                        .then(() => {
                            this.isMapboxMapReady = true;
                        });
                },
                immediate: true,
            },
            isMapboxMapReady(val) {
                if (!val) {
                    return;
                }

                this.$nextTick(() => {
                    if (!this.activeLocation) {
                        this.fitBounds({
                            center: getCoordsFromLocation(this.centerLocation),
                            duration: 0,
                        });
                    }
                });
            },
            activeLocation: {
                handler(val, prevVal) {
                    if (val) {
                        this.lastLocation = val;
                    }

                    if (!this.isMapboxMapReady || !val || !prevVal) {
                        return;
                    }

                    this.$nextTick(() => {
                        const center = getCoordsFromLocation(val);
                        const offset = this.centerOffset();

                        helpers.easeTo({ center, offset });
                    });
                },
            },
            pageType: {
                handler() {
                    if (!this.isMapboxMapReady) {
                        return;
                    }

                    this.$nextTick(() => {
                        const center = getCoordsFromLocation(this.activeLocation || this.lastLocation);
                        const offset = this.centerOffset();

                        helpers.easeTo({
                            center,
                            offset,
                            //zoom: this.activeLocation ? 15 : undefined,
                        });
                    });
                },
            },
            isHomeIntroDismissed: {
                handler() {
                    if (!this.isMapboxMapReady) {
                        return;
                    }

                    this.$nextTick(() => {
                        this.fitBounds();
                    });
                },
            },
        },
        mounted() {
            this.$log.info('Map is being mounted...');

            this.$on('zoom-changed', val => {
                this.mapZoom = val;
            });
        },
        beforeDestroy() {
            this.$log.info('Map is being destroyed...');
            this.isMapboxMapReady = false;
            helpers.unloadMap();
        },
    };
</script>



// WEBPACK FOOTER //
// vehi-industriepfad/assets/src/js/app/framework/components/mapbox-map/index.vue?2803497b